import React, { forwardRef, useCallback } from 'react';
import { pick } from 'ramda';
import { Field, useForm } from 'react-final-form';

import {
    FieldNameContext,
    useWrappedHandler,
    useWrappedInput
} from 'js/components/common/Fields/withField';
import { useRegistrationValidationEvent } from 'js/app/components/main/Registration/hooks/useRegistrationValidationEvent';

export * from 'js/components/common/Fields/withField';

export const useWrappedHandlers = ({
    type,
    input,
    onChange: onChangeCustom,
    onBlur: onBlurCustom,
    onFocus: onFocusCustom
}) => {
    const {
        onChange: onChangeRff,
        onBlur: onBlurRff,
        onFocus: onFocusRff
    } = input;

    const form = useForm();

    const handlerValidationEvent = useRegistrationValidationEvent({ fieldName: input.name });
    const onBlur = useWrappedHandler(onBlurRff, onBlurCustom, handlerValidationEvent);
    const onFocus = useWrappedHandler(onFocusRff, onFocusCustom, form.mutators.focusField);
    // Fix for iOS: no blur event for checkbox so final-form cannot perform validation.
    // We need to fire blur handler manually after change event.
    const onChangeCheckbox = useWrappedHandler(onBlur, form.mutators.focusField);
    const onChange = useWrappedHandler(onChangeRff, onChangeCustom, ['checkbox', 'radio'].includes(type) ? onChangeCheckbox : null);

    return useWrappedInput(input, onChange, onBlur, onFocus);
};

/* core, buzz added ref  */
export const withField = Wrapped => forwardRef(({
    // RFF Field props
    afterSubmit,
    allowNull,
    beforeSubmit,
    component, // silently omitting since we already use Field's children for rendering
    data,
    defaultValue,
    format,
    formatOnBlur,
    initialValue,
    isEqual,
    multiple,
    name,
    parse,
    render, // silently omitting since we already use Field's children for rendering
    subscription,
    type,
    validate,
    validateFields,
    value, // only for type=checkbox & type=radio (see RFF docs for more info)

    onChange,
    onBlur,
    onFocus,
    enableSelfValueChange,
    ...componentRestProps
}, ref) => {
    // omit `children`, `component` and `render` - user cannot change render method for RFF Field
    const rffFieldProps = {
        afterSubmit,
        allowNull,
        beforeSubmit,
        data,
        defaultValue,
        format,
        formatOnBlur,
        initialValue,
        isEqual,
        multiple,
        name,
        parse,
        ref,
        subscription,
        type,
        validate,
        validateFields,
        value
    };
    const { change, getFieldState } = useForm();
    const setValue = useCallback((val) => change(name, val), [change, name]);
    const selfChangeProps = enableSelfValueChange ? {
        parse,
        setValue
    } : {};

    return (
        <Field {...rffFieldProps}>
            {(renderProps) => {
                const { input, meta } = renderProps;
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const wrappedInput = useWrappedHandlers({ type, input, onChange, onBlur, onFocus });
                const metaFromState = pick(Object.keys(meta), getFieldState(name) || {});

                return (
                    <FieldNameContext.Provider value={name}>
                        <Wrapped
                            {...{
                                ...componentRestProps,
                                ...renderProps, /* { input, meta } */
                                ...selfChangeProps,
                                meta: { ...meta, ...metaFromState, value: input.value },
                                name,
                                type,
                                input: wrappedInput
                            }}
                            ref={ref}
                        />
                    </FieldNameContext.Provider>
                );
            }}
        </Field>
    );
});

export default withField;
